// Money display for the business the user is working in; same rules as the
// mobile app's utils/currency.ts. The nav sets the currency when it loads the
// user's businesses; everything that shows an amount goes through here.
import { useSyncExternalStore } from "react";
import { COUNTRY_CURRENCY } from "../types/countryCurrency";

const DEFAULT_CURRENCY = "USD";
const SAVED_CURRENCY_KEY = "active_currency";

const SYMBOLS: Record<string, string> = {};
for (const entry of COUNTRY_CURRENCY) {
  if (!SYMBOLS[entry.currencyCode]) SYMBOLS[entry.currencyCode] = entry.currencySymbol;
}

/** Currencies that have no cents. */
const ZERO_DECIMAL = new Set(["JPY", "KRW", "VND", "CLP", "ISK"]);

const readSaved = () => {
  try {
    return localStorage.getItem(SAVED_CURRENCY_KEY) || DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
};

let active = readSaved();
const listeners = new Set<() => void>();

export const getActiveCurrency = () => active;

export const setActiveCurrency = (currency?: string | null) => {
  const next = (currency || DEFAULT_CURRENCY).toUpperCase();
  if (next === active) return;
  active = next;
  try {
    localStorage.setItem(SAVED_CURRENCY_KEY, next);
  } catch {
    // storage unavailable: still used for this visit
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Re-renders the caller when the active currency changes. */
export const useActiveCurrency = () => useSyncExternalStore(subscribe, getActiveCurrency);

/** "$", "€", "kr"; the code itself for currencies we don't list. */
export const currencySymbol = (currency: string = active) => SYMBOLS[currency] ?? currency;

/** Digits after the decimal point: 0 for yen, 2 for most currencies. */
export const currencyDecimals = (currency: string = active) =>
  ZERO_DECIMAL.has(currency) ? 0 : 2;

const formatters = new Map<number, Intl.NumberFormat>();
const numberFormatter = (decimals: number) => {
  let formatter = formatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formatters.set(decimals, formatter);
  }
  return formatter;
};

/** Symbol before the number; word symbols ("kr", "CHF", "د.إ") get a space. */
const withSymbol = (number: string, currency: string) => {
  const symbol = currencySymbol(currency);
  return /[A-Za-z؀-ۿ]$/.test(symbol) ? `${symbol} ${number}` : `${symbol}${number}`;
};

/**
 * "€1,234.50", "-£12.00", "¥1,500".
 * `decimals: 0` for whole amounts (chart axes); `signed` shows "+" too.
 */
export const formatMoney = (
  value: number | null | undefined,
  options: { currency?: string; decimals?: number; signed?: boolean } = {}
) => {
  const currency = options.currency || active;
  const decimals = options.decimals ?? currencyDecimals(currency);
  const num = Number(value) || 0;
  const rounded = Number(Math.abs(num).toFixed(decimals));
  const text = withSymbol(numberFormatter(decimals).format(rounded), currency);
  if (rounded === 0) return text;
  if (num < 0) return `-${text}`;
  return options.signed ? `+${text}` : text;
};
